import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Button,
} from "@mui/material";
import { useContext } from "react";
import { styled } from "@mui/system";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import ConfirmationDialog from "../VersatileComponents/ConfirmationDialog";
import GivenAccessoriesToDeliveryGuyEditForm from "./edit_accessories_given_to_delivery_guys_Form";
import DeleteGivenAccessoriesToDeliveryGuys from "../../api/accessories_given_to_workers/delete_given_accessories_to_deliveryGuy.jsx";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "1.5rem",
  backgroundColor: theme.palette.background.alt,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  transition: "transform 0.2s ease-in-out, height 0.3s ease",
  position: "relative",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
  },
}));

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" mb={1}>
    <Typography color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography color="text.primary" fontWeight={600} textAlign="right">
      {value}
    </Typography>
  </Box>
);

const MAX_ITEMS_VISIBLE = 3;

const GivenAccessoryToDeliveryGuyTable = ({ givenAccessory }) => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { setIsSubmitting } = useContext(SpinnerContext);
  const { openSnackbar } = useSnackbar();
  const [openDialog, setOpenDialog] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);

  const needsExpansion =
    givenAccessory.accessoriesGiven.length > MAX_ITEMS_VISIBLE;
  const itemsToDisplay =
    needsExpansion && !isExpanded
      ? givenAccessory.accessoriesGiven.slice(0, MAX_ITEMS_VISIBLE)
      : givenAccessory.accessoriesGiven;

  const handleDeleteIconClick = () => {
    handleMenuClose();
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleEdit = () => {
    handleMenuClose();
    setDialogOpen(true);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteConfirmed = async () => {
    handleDialogClose();
    setIsSubmitting(true);
    try {
      const res = await DeleteGivenAccessoriesToDeliveryGuys(
        user,
        givenAccessory.id
      );
      openSnackbar(res.data.message, "success");
    } catch (error) {
      if (error.response && error.response.data) {
        openSnackbar(
          error.response.data.message,
          error.response.data.type ? error.response.data.type : "error"
        );
      } else {
        openSnackbar(
          "An unexpected error occurred.Please kindly check your connection.",
          "error"
        );
      }
    }
    setIsSubmitting(false);
    handleMenuClose();
  };

  return (
    <>
      <StyledCard>
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <MoreVertIcon />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
          {givenAccessory.returned && (
            <MenuItem onClick={handleDeleteIconClick}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
            </MenuItem>
          )}
        </Menu>

        <CardContent sx={{ p: 5 }}>
          <InfoRow
            label="Delivery Guy Name"
            value={givenAccessory.deliveryGuyName}
          />
          <Divider sx={{ my: 1.5 }} />
          <InfoRow label="Notes" value={givenAccessory.notes || "N/A"} />
          <Divider sx={{ my: 1.5 }} />

          <Box mb={1}>
            <Typography color="text.secondary" fontWeight={600} mb={1}>
              Accessories Given
            </Typography>
            <Box pl={2} pr={2}>
              {/* Header Row */}
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography color="text.secondary" fontWeight={600}>
                  Accessory Type
                </Typography>
                <Typography color="text.secondary" fontWeight={600}>
                  Quantity
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />

              {itemsToDisplay && itemsToDisplay.length > 0 ? (
                itemsToDisplay.map((item, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    mb={0.5}
                  >
                    <Typography color="text.primary">
                      - {item.accessoryType}
                    </Typography>
                    <Typography color="text.primary" fontWeight={500}>
                      {item.quantity}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary" fontStyle="italic">
                  No accessories listed.
                </Typography>
              )}
            </Box>
          </Box>

          {needsExpansion && (
            <Box display="flex" justifyContent="center" mt={1}>
              <Button
                size="small"
                onClick={() => setIsExpanded(!isExpanded)}
                endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {isExpanded ? "See Less" : "See More"}
              </Button>
            </Box>
          )}

          <Box mt={2} display="flex" justifyContent="center">
            <Chip
              label={givenAccessory.returned ? "Returned" : "Manage Return"}
              color={givenAccessory.returned ? "success" : "primary"}
              onClick={givenAccessory.returned ? undefined : handleEdit}
              disabled={!!givenAccessory.returned}
              sx={{ fontWeight: 600, fontSize: 16, width: 180 }}
            />
          </Box>
        </CardContent>
        {dialogOpen && (
          <GivenAccessoriesToDeliveryGuyEditForm
            givenAccessory={givenAccessory}
            isEditDialogOpen={dialogOpen}
            closeEditDialog={() => setDialogOpen(false)}
          />
        )}
        <ConfirmationDialog
          open={openDialog}
          handleDialogClose={handleDialogClose}
          handleConfirmed={handleDeleteConfirmed}
          message={`Are you sure you want to delete this accessory record for ${givenAccessory.deliveryGuyName}? This action cannot be undone.`}
          title={`Confirm Deletion`}
        />
      </StyledCard>
    </>
  );
};

export default GivenAccessoryToDeliveryGuyTable;
